import type { NoteTemplate } from './progress';
import type { StudentClassChip } from './scheduling';
export type PricingType = 'PER_SESSION' | 'MONTHLY_FLAT';
export type BillingPeriodKind = 'MONTHLY' | 'TERM';

/**
 * How a student pays — the teacher-facing name for the (pricingType,
 * billingPeriod) pair. Three combinations are real:
 *   SESSION_MONTHLY — trả theo buổi, chốt sổ hàng tháng
 *   SESSION_TERM    — trả theo khoá (mua trước N buổi)
 *   MONTHLY_FLAT    — học phí tháng cố định (billingPeriod is always MONTHLY)
 *
 * It exists so the Học viên filter's chips, the API's Prisma clause and any
 * future enrolment label all name the same three buckets. Two representations
 * of one rule is exactly how they drift; `students.filters.test.ts` pins this
 * one against the clause the route builds.
 */
export type EnrollmentMode = 'SESSION_MONTHLY' | 'SESSION_TERM' | 'MONTHLY_FLAT';

/** Every mode, in the order the filter chips render them. */
export const ENROLLMENT_MODES: readonly EnrollmentMode[] = [
  'SESSION_MONTHLY',
  'SESSION_TERM',
  'MONTHLY_FLAT',
] as const;

/**
 * Pure. `pricingType` is tested first so the typed-but-impossible
 * MONTHLY_FLAT + TERM row still lands in MONTHLY_FLAT rather than in no
 * bucket at all — a filter that silently drops rows is worse than one that
 * puts an odd row somewhere defensible.
 */
export function enrollmentModeOf(e: {
  pricingType: PricingType;
  billingPeriod: BillingPeriodKind;
}): EnrollmentMode {
  if (e.pricingType === 'MONTHLY_FLAT') return 'MONTHLY_FLAT';
  return e.billingPeriod === 'TERM' ? 'SESSION_TERM' : 'SESSION_MONTHLY';
}

export type StudentStatus = 'ACTIVE' | 'INACTIVE';
export type EnrollmentStatus = 'ACTIVE' | 'PAUSED' | 'ENDED';
export type Gender = 'MALE' | 'FEMALE';

export interface Subject {
  id: string;
  organizationId: string;
  name: string;
  defaultRate: number | null;
  defaultMonthlyRate: number | null;
  /** THI-193: the subject's note template (sườn nhận xét) or `null` for the
   *  free-text session-note form. */
  noteTemplate: NoteTemplate | null;
  /** THI-346: whether the session-note form offers the 1..5 level and the
   *  timeline / parent page show it. */
  sessionRatingEnabled: boolean;
  createdAt: string;
}

export interface Student {
  id: string;
  organizationId: string;
  /**
   * NOT NULL in the database. Null on the wire only when the API stripped it:
   * `GET /students/:id` nulls both `clientId` and `client` for a role without
   * `manage:clients` (THI-129), so the payer is not reachable by deep link.
   */
  clientId: string | null;
  /** Họ và tên đầy đủ. The primary name everywhere — lists, invoices, attendance. */
  name: string;
  /** Tên thường gọi — the informal name the family uses. Optional, never a substitute for `name`. */
  nickname: string | null;
  tags: string[];
  dateOfBirth: string | null;
  /** Nam/Nữ. Null means not stated — the state of every student created before
   *  the field existed. */
  gender: Gender | null;
  status: StudentStatus;
  notes: string | null;
  createdAt: string;
}

export interface StudentWithClient extends Student {
  /** Null for a role without `manage:clients` — see `Student.clientId`. */
  client: { id: string; name: string; zaloPhone: string | null; email: string | null } | null;
  /**
   * Only present on `PUT /students/:id` responses where `clientId` changed (payer
   * reassignment, I8): count of the student's still-unpaid (PENDING/PARTIAL)
   * invoices, which remain billed to the *previous* payer. FE uses this to show a
   * heads-up after the reassignment has already succeeded.
   */
  pendingInvoiceCount?: number;
  /** Subjects this student has an ACTIVE enrolment in — real enrolments, not
   *  `tags`. The list's MÔN HỌC column renders these. Empty means the student
   *  exists but was never enrolled, which is what `hasEnrollment: false` says. */
  subjects?: { id: string; name: string }[];
  /** False when the student has no ACTIVE enrolment at all. */
  hasEnrollment?: boolean;
  /** True when at least one ACTIVE enrolment sits in no live slot — a student
   *  enrolled in Piano (assigned) and Guitar (not) is `true`, because the
   *  Guitar half was forgotten. */
  needsSlot?: boolean;
  /**
   * List-only (GET /students). The SMALLEST `TermState.remaining` across this
   * student's ACTIVE/PAUSED enrolments that have an open khoá — the same
   * population `termNudgeState` nudges on — or null when they have none.
   * May be **negative**: a khoá is a billing unit, not a gate, and the overage
   * is exactly what the badge needs to show (see `TermState.remaining`).
   */
  khoaRemaining?: number | null;
  /** THI-267 Task 6: the classes (ClassGroup) this student has a live
   *  membership in, derived from its ACTIVE enrolments' live slot
   *  memberships — one chip per class, deduped, with `joinedSlots` /
   *  `totalSlots` driving the "(2/3)" partial-membership text. */
  classes?: StudentClassChip[];
}

/** A row from the AuditLog table, as returned by GET /students/:id/audit. */
export interface AuditLogEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  metadata: unknown;
  actorUserId: string | null;
  createdAt: string;
}

export interface Enrollment {
  id: string;
  organizationId: string;
  studentId: string;
  subjectId: string;
  pricingType: PricingType;
  ratePerSession: number | null;
  monthlyRate: number | null;
  billingPeriod: BillingPeriodKind;
  termSize: number | null;
  status: EnrollmentStatus;
  chargeAbsent: boolean;
  startedAt: string;
  endedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Result of `POST /enrollments/charge-absent/bulk` — org-wide re-apply of the
 *  absence-billing rule to every existing PER_SESSION enrollment. `monthly`/
 *  `term` are counted before the update runs (see the route), since afterward
 *  the touched rows are indistinguishable from ones that already held the value. */
export interface ChargeAbsentBulkResult {
  updated: number;
  monthly: number;
  term: number;
}

export interface TermState {
  termSize: number;
  currentLabel: string | null;
  /** Sessions of the block still unused (termSize - consumed). A khoá is a billing unit,
   *  not a gate: this may be **negative** on an over-run and is deliberately not clamped —
   *  the overage is what the teacher needs to see, and it bills as a debit at renewal. */
  remaining: number;
  /** Billable-attended session count so far in the current OPEN term. Drives the "còn X buổi" UI display + nudge. */
  consumed: number;
  hasOpenTerm: boolean;
}

export interface EnrollmentWithSubject extends Enrollment {
  subject: { id: string; name: string };
  /** `nickname` mirrors `Student.nickname` (THI-82) — feeds the AddStudentCombobox's
   *  name+nickname match and `name (nickname)` display. */
  student: { id: string; name: string; nickname: string | null };
  attendedCount: number; // count of PRESENT session participants (M-C)
  /** Non-null only for PER_SESSION + billingPeriod=TERM enrollments (M-D.2). */
  termState: TermState | null;
  /**
   * Effective "start" shown on the enrollment card (Polish-A §4.4): PER_SESSION+MONTHLY ->
   * earliest active SlotEnrollment.joinedAt (null if not yet xếp lớp); PER_SESSION+TERM -> the
   * current OPEN term's BillingPeriod.startAt (null if no open term); MONTHLY_FLAT -> createdAt.
   */
  startDate: string | null;
  /**
   * The weekly fixed slots this enrollment currently sits in, ordered Mon→Sun
   * then by time. Empty when the student has not been xếp lớp yet — which is
   * what the card's "Chưa xếp lớp" state means.
   */
  slots: {
    id: string;
    /** 1 = Monday … 7 = Sunday. */
    dayOfWeek: number;
    /** "HH:mm". */
    startTime: string;
    durationMin: number;
    teacherId: string;
  }[];
  /** THI-267 Task 6/14: the classes (ClassGroup) this enrollment has a live
   *  membership in — same chip shape and same `classChipsFrom` derivation as
   *  `StudentWithClient.classes` above, computed from the SAME
   *  `slotEnrollments` relation `slots` reads (`GET /enrollments` route).
   *  Empty/undefined is the same "Chưa xếp lớp" state `slots` being empty
   *  already means. */
  classes?: StudentClassChip[];
}
